import { useEffect, useRef, useState } from "react";
import * as clientsApi from "../../api/clients";
import { Cliente } from "../../api/clients";
import { Input } from "./Input";

interface ClienteAutocompleteProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Chiamato quando l'utente sceglie un suggerimento — utile per precompilare altri campi dal cliente trovato. */
  onSelect?: (cliente: Cliente) => void;
}

/**
 * Campo di testo libero con suggerimenti dall'anagrafica clienti esistente.
 * GET /api/clienti è riservato ad admin/staff — per gli altri ruoli (es.
 * centro_assistenza, che può comunque creare Sopralluoghi/Interventi) la
 * ricerca fallisce silenziosamente: il campo resta un input di testo
 * normale, senza suggerimenti, mai bloccante.
 */
export function ClienteAutocomplete({ label, value, onChange, onSelect }: ClienteAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<Cliente[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const query = value.trim();
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const { clienti } = await clientsApi.listClienti(query);
        setSuggestions(clienti.slice(0, 8));
      } catch {
        setSuggestions([]);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSelect(cliente: Cliente) {
    onChange(cliente.nome);
    onSelect?.(cliente);
    setSuggestions([]);
    setIsOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        label={label}
        value={value}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
      />

      {isOpen && suggestions.length > 0 && (
        <ul className="absolute top-full z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-line bg-white shadow-card">
          {suggestions.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => handleSelect(c)}
                className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-primary/5"
              >
                <span className="font-medium text-ink">{c.nome}</span>
                {(c.insegna || c.comune) && (
                  <span className="text-xs text-ink/50">{[c.insegna, c.comune].filter(Boolean).join(" · ")}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
